// hooks/useFilters.js
import { useState, useEffect } from 'react';
import { useEnums } from './useEnums';
import { analytics } from '../services/analytics';
import { shouldExcludeProduct, shouldIncludeProduct } from '../utils/ingredientUtils';

// Helper functions
const normalizeString = (str) => {
  if (str == null) return '';
  return str.trim().toLowerCase();
};

const DRY_MATTER_NUTRIENT_FIELDS = [
  'proteinDryMatter',
  'phosphorusDryMatter',
  'carbsDryMatter',
  'sodiumDryMatter',
  'calciumDryMatter',
  'fatDryMatter',
  'fiberDryMatter',
  'potassiumDryMatter',
  'ashDryMatter'
];

// Custom hook for filtering data
export const useFilters = (initialData, setFilteredRows) => {
  const enums = useEnums();
  // Text filters
  const [searchText, setSearchText] = useState('');
  const [searchProteinText, setSearchProteinText] = useState('');
  
  // Multi-select filters
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedProteins, setSelectedProteins] = useState([]);
  const [selectedLifestages, setSelectedLifestages] = useState([]);
  const [selectedTextures, setSelectedTextures] = useState([]);
  const [selectedHealthBenefits, setSelectedHealthBenefits] = useState([]);
  const [selectedFormats, setSelectedFormats] = useState([]);
  const [excludedIngredients, setExcludedIngredients] = useState([]);
  const [includedIngredients, setIncludedIngredients] = useState([]);
  const [excludedProteins, setExcludedProteins] = useState([]);
  
  // Range filters
  const [minCalories, setMinCalories] = useState('');
  const [maxCalories, setMaxCalories] = useState('');
  const [minProtein, setMinProtein] = useState('');
  const [maxProtein, setMaxProtein] = useState('');
  const [minCarbs, setMinCarbs] = useState('');
  const [maxCarbs, setMaxCarbs] = useState('');
  const [minPhosphorus, setMinPhosphorus] = useState('');
  const [maxPhosphorus, setMaxPhosphorus] = useState('');
  const [minSodium, setMinSodium] = useState('');
  const [maxSodium, setMaxSodium] = useState('');
  const [minCalcium, setMinCalcium] = useState('');
  const [maxCalcium, setMaxCalcium] = useState('');
  const [minFat, setMinFat] = useState('');
  const [maxFat, setMaxFat] = useState('');
  
  // Boolean filters
  const [selectedGrainFree, setSelectedGrainFree] = useState([]);
  const [selectedGmoFree, setSelectedGmoFree] = useState([]);
  const [selectedRawFood, setSelectedRawFood] = useState([]);
  const [selectedPrescription, setSelectedPrescription] = useState([]);
  const [selectedLid, setSelectedLid] = useState([]);
  const [includeGuaranteedAnalysis, setIncludeGuaranteedAnalysis] = useState([]);
  const [noveltyProteins, setNoveltyProteins] = useState([]);
  const [singleProtein, setSingleProtein] = useState([]);
  const [hydrolyzedProtein, setHydrolyzedProtein] = useState([]);

  // Consolidated filter state object for easier access
  const filters = {
    text: { searchText, searchProteinText },
    multiSelect: {
      brands: selectedBrands,
      categories: selectedCategories,
      proteins: selectedProteins,
      excludedProteins: excludedProteins,
      lifestages: selectedLifestages,
      textures: selectedTextures,
      healthBenefits: selectedHealthBenefits,
      formats: selectedFormats,
      excludedIngredients: excludedIngredients,
      includedIngredients: includedIngredients
    },
    range: {
      calories: { min: minCalories, max: maxCalories },
      protein: { min: minProtein, max: maxProtein },
      carbs: { min: minCarbs, max: maxCarbs },
      phosphorus: { min: minPhosphorus, max: maxPhosphorus },
      sodium: { min: minSodium, max: maxSodium },
      calcium: { min: minCalcium, max: maxCalcium },
      fat: { min: minFat, max: maxFat }
    },
    boolean: {
      grainFree: selectedGrainFree,
      gmoFree: selectedGmoFree,
      rawFood: selectedRawFood,
      prescription: selectedPrescription,
      lid: selectedLid,
      includeGuaranteedAnalysis: includeGuaranteedAnalysis,
      noveltyProteins: noveltyProteins,
      singleProtein: singleProtein,
      hydrolyzedProtein: hydrolyzedProtein
    }
  };

  // Function to set a specific filter
  const setFilter = (filterType, filterName, value) => {
    // Log the filter change
    analytics.logFilterChange(filterType, filterName, value);

    switch (filterType) {
      case 'text':
        if (filterName === 'searchText') setSearchText(value);
        else if (filterName === 'searchProteinText') setSearchProteinText(value);
        break;
      case 'multiSelect':
        if (filterName === 'brands') setSelectedBrands(value);
        else if (filterName === 'categories') setSelectedCategories(value);
        else if (filterName === 'proteins') setSelectedProteins(value);
        else if (filterName === 'excludedProteins') setExcludedProteins(value);
        else if (filterName === 'lifestages') setSelectedLifestages(value);
        else if (filterName === 'textures') setSelectedTextures(value);
        else if (filterName === 'healthBenefits') setSelectedHealthBenefits(value);
        else if (filterName === 'formats') setSelectedFormats(value);
        else if (filterName === 'excludedIngredients') setExcludedIngredients(value);
        else if (filterName === 'includedIngredients') setIncludedIngredients(value);
        break;
      case 'range':
        if (filterName === 'calories') {
          if ('min' in value) setMinCalories(value.min);
          if ('max' in value) setMaxCalories(value.max);
        }
        else if (filterName === 'protein') {
          if ('min' in value) setMinProtein(value.min);
          if ('max' in value) setMaxProtein(value.max);
        }
        else if (filterName === 'carbs') {
          if ('min' in value) setMinCarbs(value.min);
          if ('max' in value) setMaxCarbs(value.max);
        }
        else if (filterName === 'phosphorus') {
          if ('min' in value) setMinPhosphorus(value.min);
          if ('max' in value) setMaxPhosphorus(value.max);
        }
        else if (filterName === 'sodium') {
          if ('min' in value) setMinSodium(value.min);
          if ('max' in value) setMaxSodium(value.max);
        }
        else if (filterName === 'calcium') {
          if ('min' in value) setMinCalcium(value.min);
          if ('max' in value) setMaxCalcium(value.max);
        }
        else if (filterName === 'fat') {
          if ('min' in value) setMinFat(value.min);
          if ('max' in value) setMaxFat(value.max);
        }
        break;
      case 'boolean':
        if (filterName === 'grainFree') setSelectedGrainFree(value);
        else if (filterName === 'gmoFree') setSelectedGmoFree(value);
        else if (filterName === 'rawFood') setSelectedRawFood(value);
        else if (filterName === 'prescription') setSelectedPrescription(value);
        else if (filterName === 'lid') setSelectedLid(value);
        else if (filterName === 'includeGuaranteedAnalysis') setIncludeGuaranteedAnalysis(value);
        else if (filterName === 'noveltyProteins') setNoveltyProteins(value);
        else if (filterName === 'singleProtein') setSingleProtein(value);
        else if (filterName === 'hydrolyzedProtein') setHydrolyzedProtein(value);
        break;
      default:
        console.error(`Unknown filter type: ${filterType}`);
    }
  };

  // Reset all filters
  const resetAllFilters = () => {
    // Log the filter reset
    analytics.logFilterReset();
    
    setSearchText('');
    setSearchProteinText('');
    setSelectedBrands([]);
    setSelectedCategories([]);
    setSelectedProteins([]);
    setExcludedProteins([]);
    setSelectedLifestages([]);
    setSelectedTextures([]);
    setSelectedHealthBenefits([]);
    setSelectedFormats([]);
    setExcludedIngredients([]);
    setIncludedIngredients([]);
    setMinCalories('');
    setMaxCalories('');
    setMinProtein('');
    setMaxProtein('');
    setMinCarbs('');
    setMaxCarbs('');
    setMinPhosphorus('');
    setMaxPhosphorus('');
    setMinSodium('');
    setMaxSodium('');
    setMinCalcium('');
    setMaxCalcium('');
    setMinFat('');
    setMaxFat('');
    setSelectedGrainFree([]);
    setSelectedGmoFree([]);
    setSelectedRawFood([]);
    setSelectedPrescription([]);
    setSelectedLid([]);
    setIncludeGuaranteedAnalysis([]);
    setNoveltyProteins([]);
    setSingleProtein([]);
    setHydrolyzedProtein([]);
  };

  // Apply filters to data
  useEffect(() => {
    if (!initialData || initialData.length === 0) return;
    
    const shouldExcludeGuaranteed = !includeGuaranteedAnalysis.includes("TRUE");

    const getNutrientValueForFiltering = (row, field) => {
      const value = row[field];
      if (value === null || value === undefined) return null;
      if (!shouldExcludeGuaranteed) return value;

      const source = row?.nutrientSources?.[field];
      return source === 'guaranteed_analysis' ? null : value;
    };

    const sanitizeRowForDisplay = (row) => {
      if (!shouldExcludeGuaranteed) return row;
      const sanitizedRow = { ...row };
      DRY_MATTER_NUTRIENT_FIELDS.forEach((field) => {
        if (row?.nutrientSources?.[field] === 'guaranteed_analysis') {
          sanitizedRow[field] = null;
        }
      });
      return sanitizedRow;
    };

    const filtered = initialData.filter(row => {
      // Brand filtering
      const normalizedRowBrand = normalizeString(row.brand);
      const matchesBrand = selectedBrands.length > 0
        ? selectedBrands.some(selectedBrand => {
          const normalizedSelectedBrand = normalizeString(enums.brands[selectedBrand]);
          return normalizedRowBrand === normalizedSelectedBrand;
        })
        : true;

      // Name filtering
      const trimmedSearchText = searchText.trim();
      const matchesName = [row.name, row.brand, row.line]
        .some(field => field?.toLowerCase().includes(trimmedSearchText.toLowerCase())) ?? false;

      // Category filtering
      const normalizedRowCategory = normalizeString(row.category);
      const matchesCategory = selectedCategories.length > 0
        ? selectedCategories.some(selectedCategory => {
          const normalizedSelectedCategory = normalizeString(enums.categories[selectedCategory]);
          return normalizedRowCategory === normalizedSelectedCategory;
        })
        : true;

      // Protein filtering
      const lowerSearchProteinText = searchProteinText.toLowerCase().trim();
      const lowerSelectedProteins = selectedProteins
        .filter(p => p != null)
        .map(p => p.toLowerCase().trim());

      const rowProteins = (row.proteins || []).map(p => p.trim().toLowerCase());

      const matchesProteinIngredients =
        (!lowerSearchProteinText || rowProteins.some(p => p.includes(lowerSearchProteinText))) &&
        (lowerSelectedProteins.length === 0 || lowerSelectedProteins.some(p => rowProteins.includes(p)));

      // Excluded proteins filtering (negation - exclude products with any selected protein)
      const lowerExcludedProteins = excludedProteins
        .filter(p => p != null)
        .map(p => p.toLowerCase().trim());
      
      const matchesExcludedProteins = lowerExcludedProteins.length === 0 || 
        !lowerExcludedProteins.some(p => rowProteins.includes(p));

      // Multi-select lifestage filtering
      let matchesLifestage = true;
      if (selectedLifestages.length > 0 && row.lifestage) {
        const rowLifestages = (row.lifestage || []).map(s => s.trim().toLowerCase());
        matchesLifestage = selectedLifestages.some(stage =>
          rowLifestages.includes(enums.lifestages[stage].toLowerCase())
        );
      }

      // Multi-select texture filtering
      let matchesTexture = true;
      if (selectedTextures.length > 0) {
        matchesTexture = selectedTextures.some(texture =>
          row.texture?.toLowerCase() === enums.textures[texture].toLowerCase()
        );
      }

      // Multi-select health benefits filtering with special kidney support hack
      let matchesHealthBenefits = true;
      if (selectedHealthBenefits.length > 0) {
        const rowHealthBenefits = (row.healthBenefits || []).map(s => s.trim().toLowerCase());

        // Special hack for kidney support filter
        if (selectedHealthBenefits.includes('Kidney Support')) {
          const hasKidneySupport = rowHealthBenefits.some(benefit => benefit.includes('kidney support'));

          const phosphorusForFiltering = getNutrientValueForFiltering(row, 'phosphorusDryMatter');
          const phosphorusSource = row?.nutrientSources?.phosphorusDryMatter;

          const lowPhosphorusNoGuaranteedAnalysis =
            phosphorusForFiltering &&
            phosphorusForFiltering < 0.6 &&
            phosphorusSource !== 'guaranteed_analysis' &&
            (!row.healthBenefits || row.healthBenefits.length === 0);

          const matchesKidney = hasKidneySupport || lowPhosphorusNoGuaranteedAnalysis;

          // Check other selected health benefits (excluding Kidney Support)
          const otherBenefits = selectedHealthBenefits.filter(b => b !== 'Kidney Support');
          if (otherBenefits.length > 0) {
            const matchesOther = otherBenefits.some(benefit =>
              rowHealthBenefits.some(rowBenefit => rowBenefit.includes(enums.healthBenefits[benefit].toLowerCase()))
            );
            matchesHealthBenefits = matchesKidney || matchesOther;
          } else {
            matchesHealthBenefits = matchesKidney;
          }
        } else {
          // Regular health benefits filtering for other benefits
          if (row.healthBenefits) {
            matchesHealthBenefits = selectedHealthBenefits.some(benefit =>
              rowHealthBenefits.some(rowBenefit => rowBenefit.includes(enums.healthBenefits[benefit].toLowerCase()))
            );
          } else {
            matchesHealthBenefits = false;
          }
        }
      }

      // Multi-select format filtering
      let matchesFormat = true;
      if (selectedFormats.length > 0) {
        if (!row.format || row.format.length === 0) {
          // If format filter is applied but product has no format, exclude it
          matchesFormat = false;
        } else {
          const rowFormats = row.format.map(s => s.trim().toLowerCase());
          matchesFormat = selectedFormats.some(format =>
            rowFormats.includes(enums.formats[format].toLowerCase())
          );
        }
      }

      // Ingredient exclusion filtering
      const matchesExcludedIngredients = !shouldExcludeProduct(row, excludedIngredients);
      
      // Ingredient inclusion filtering
      const matchesIncludedIngredients = shouldIncludeProduct(row, includedIngredients);

      // Numerical filters
      const matchesCalories = (minCalories !== '' ? row.caloriesPerKg && row.caloriesPerKg >= Number(minCalories) : true) &&
        (maxCalories !== '' ? row.caloriesPerKg && row.caloriesPerKg <= Number(maxCalories) : true);

      const proteinForFiltering = getNutrientValueForFiltering(row, 'proteinDryMatter');
      const phosphorusForFiltering = getNutrientValueForFiltering(row, 'phosphorusDryMatter');
      const sodiumForFiltering = getNutrientValueForFiltering(row, 'sodiumDryMatter');
      const carbsForFiltering = getNutrientValueForFiltering(row, 'carbsDryMatter');
      const calciumForFiltering = getNutrientValueForFiltering(row, 'calciumDryMatter');
      const fatForFiltering = getNutrientValueForFiltering(row, 'fatDryMatter');

      const matchesProtein = (minProtein !== '' ? proteinForFiltering && proteinForFiltering >= Number(minProtein) : true) &&
        (maxProtein !== '' ? proteinForFiltering && proteinForFiltering <= Number(maxProtein) : true);

      const matchesPhosphorus = (minPhosphorus !== '' ? phosphorusForFiltering && phosphorusForFiltering >= Number(minPhosphorus) : true) &&
        (maxPhosphorus !== '' ? phosphorusForFiltering && phosphorusForFiltering <= Number(maxPhosphorus) : true);

      const matchesSodium = (minSodium !== '' ? sodiumForFiltering && sodiumForFiltering >= Number(minSodium) : true) &&
        (maxSodium !== '' ? sodiumForFiltering && sodiumForFiltering <= Number(maxSodium) : true);

      const matchesCarbs = (minCarbs !== '' ? carbsForFiltering && carbsForFiltering >= Number(minCarbs) : true) &&
        (maxCarbs !== '' ? carbsForFiltering && carbsForFiltering <= Number(maxCarbs) : true);

      const matchesCalcium = (minCalcium !== '' ? calciumForFiltering && calciumForFiltering >= Number(minCalcium) : true) &&
        (maxCalcium !== '' ? calciumForFiltering && calciumForFiltering <= Number(maxCalcium) : true);

      const matchesFat = (minFat !== '' ? fatForFiltering && fatForFiltering >= Number(minFat) : true) &&
        (maxFat !== '' ? fatForFiltering && fatForFiltering <= Number(maxFat) : true);

      // Boolean filters - now with multi-select
      let matchesIsGrainFree = true;
      if (selectedGrainFree.length > 0) {
        if (selectedGrainFree.includes("TRUE") && !selectedGrainFree.includes("FALSE")) {
          matchesIsGrainFree = row.isGrainFree === true;
        } else if (!selectedGrainFree.includes("TRUE") && selectedGrainFree.includes("FALSE")) {
          matchesIsGrainFree = row.isGrainFree === false;
        }
      }

      let matchesIsGmoFree = true;
      if (selectedGmoFree.length > 0) {
        if (selectedGmoFree.includes("TRUE")) {
          matchesIsGmoFree = row.isGmoFree === true;
        }
      }

      let matchesContainsRawFood = true;
      if (selectedRawFood.length > 0) {
        if (selectedRawFood.includes("TRUE") && !selectedRawFood.includes("FALSE")) {
          matchesContainsRawFood = row.containsRawFood === true;
        } else if (!selectedRawFood.includes("TRUE") && selectedRawFood.includes("FALSE")) {
          matchesContainsRawFood = row.containsRawFood === false;
        }
      }

      let matchesRequiresPrescription = true;
      if (selectedPrescription.length > 0) {
        if (selectedPrescription.includes("TRUE") && !selectedPrescription.includes("FALSE")) {
          matchesRequiresPrescription = row.isPrescription === true;
        } else if (!selectedPrescription.includes("TRUE") && selectedPrescription.includes("FALSE")) {
          matchesRequiresPrescription = row.isPrescription === false;
        }
      }

      // LID filter
      let matchesIsLid = true;
      if (selectedLid.length > 0) {
        if (selectedLid.includes("TRUE") && !selectedLid.includes("FALSE")) {
          matchesIsLid = row.isLid === true;
        } else if (!selectedLid.includes("TRUE") && selectedLid.includes("FALSE")) {
          matchesIsLid = row.isLid === false;
        }
      }

      // Novelty Proteins filter
      let matchesNoveltyProteins = true;
      if (noveltyProteins.length > 0) {
        if (!row.proteins) {
          // If product has no proteins, exclude it when filter is active
          matchesNoveltyProteins = false;
        } else {
          const rowProteins = (row.proteins || []).map(p => p.trim().toLowerCase());

          const noveltyProteinsLower = enums.noveltyProteinList.map(np => np.toLowerCase());
          
          // Check if any product protein matches any novelty protein
          const hasNoveltyProtein = rowProteins.some(protein => 
            noveltyProteinsLower.includes(protein)
          );
          
          // Check if all product proteins are novelty proteins
          const isNovelOnly = rowProteins.length > 0 && rowProteins.every(protein => 
            noveltyProteinsLower.includes(protein)
          );
          
          if (noveltyProteins.includes("TRUE") && noveltyProteins.includes("NOVEL_ONLY")) {
            // If both options are selected, show products that match either condition
            matchesNoveltyProteins = hasNoveltyProtein || isNovelOnly;
          } else if (noveltyProteins.includes("TRUE")) {
            // "Contains Novel Protein" - product has at least one novelty protein
            matchesNoveltyProteins = hasNoveltyProtein;
          } else if (noveltyProteins.includes("NOVEL_ONLY")) {
            // "Novel-only" - product has ONLY novelty proteins
            matchesNoveltyProteins = isNovelOnly;
          } else {
            matchesNoveltyProteins = false;
          }
        }
      }

      // Single Protein filter
      let matchesSingleProtein = true;
      if (singleProtein.length > 0) {
        if (singleProtein.includes("TRUE")) {
          if (!row.proteins || row.proteins.length === 0) {
            // If product has no proteins, exclude it
            matchesSingleProtein = false;
          } else {
            const rowProteins = (row.proteins || []).map(p => p.trim()).filter(p => p.length > 0);
            
            // Check if product has exactly one protein
            matchesSingleProtein = rowProteins.length === 1;
          }
        }
      }

      // Hydrolyzed Protein filter
      let matchesHydrolyzedProtein = true;
      if (hydrolyzedProtein.length > 0) {
        if (hydrolyzedProtein.includes("TRUE")) {
          matchesHydrolyzedProtein = row.isHydrolyzedProtein === true;
        }
      }

      // Combine all filters
      return matchesBrand && matchesName && matchesCategory && matchesProteinIngredients && matchesExcludedProteins &&
        matchesLifestage && matchesTexture && matchesHealthBenefits && matchesFormat && matchesExcludedIngredients && matchesIncludedIngredients &&
        matchesCalories && matchesProtein && matchesPhosphorus && matchesSodium && matchesCarbs &&
        matchesCalcium && matchesFat &&
        matchesIsGrainFree && matchesIsGmoFree && matchesContainsRawFood && matchesRequiresPrescription &&
        matchesIsLid && matchesNoveltyProteins && matchesSingleProtein && matchesHydrolyzedProtein;
    });

    setFilteredRows(filtered.map(sanitizeRowForDisplay));
  }, [
    initialData,
    setFilteredRows,
    searchText,
    searchProteinText,
    selectedBrands,
    selectedCategories,
    selectedProteins,
    excludedProteins,
    selectedLifestages,
    selectedTextures,
    selectedHealthBenefits,
    selectedFormats,
    excludedIngredients,
    includedIngredients,
    minCalories,
    maxCalories,
    minProtein,
    maxProtein,
    minCarbs,
    maxCarbs,
    minPhosphorus,
    maxPhosphorus,
    minSodium,
    maxSodium,
    minCalcium,
    maxCalcium,
    minFat,
    maxFat,
    selectedGrainFree,
    selectedGmoFree,
    selectedRawFood,
    selectedPrescription,
    selectedLid,
    includeGuaranteedAnalysis,
    noveltyProteins,
    singleProtein,
    hydrolyzedProtein,
    enums
  ]);

  return { filters, setFilter, resetAllFilters };
};